<template>
  <div class="contents">
    <div class="layout view-count-analysis">
      <!-- 페이지 헤더 -->
      <div class="page-header">
        <h1 class="page-title">조회수 통계</h1>
        <p class="page-description">컨텐츠별 조회수 통계를 확인하고 분석할 수 있습니다.</p>
      </div>

      <!-- 필터 섹션 -->
      <FilterSection>
        <template #filters>
          <div class="filter-item">
            <label for="contentType">컨텐츠 타입</label>
            <select 
              id="contentType" 
              v-model="selectedContentType" 
              @change="changeContentType(selectedContentType)"
              class="form-select"
            >
              <option 
                v-for="option in contentTypeOptions" 
                :key="option.value" 
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
          
          <div class="filter-item">
            <label for="sortBy">정렬 기준</label>
            <select 
              id="sortBy" 
              :value="sortBy" 
              @change="handleSortChange"
              class="form-select"
            >
              <option value="viewCounts">조회수</option>
              <option value="createdAt">생성일</option>
              <option value="id">ID</option>
            </select>
          </div>
          
          <div class="filter-item">
            <label for="sortDirection">정렬 순서</label>
            <select 
              id="sortDirection" 
              :value="sortDirection" 
              @change="handleSortDirectionChange"
              class="form-select"
            >
              <option value="desc">내림차순</option>
              <option value="asc">오름차순</option>
            </select>
          </div>

          <div class="filter-item">
            <button 
              @click="refreshData" 
              :disabled="isLoading"
              class="refresh-button"
              title="데이터 새로고침"
            >
              <span v-if="isLoading">🔄</span>
              <span v-else>🔄</span>
              새로고침
            </button>
          </div>
        </template>
      </FilterSection>

      <!-- 에러 상태 -->
      <div v-if="error" class="error-message">
        <span class="error-icon">⚠️</span>
        <span class="error-text">{{ error }}</span>
        <button @click="refreshData" class="retry-button">다시 시도</button>
      </div>

      <!-- 통계 카드 섹션 -->
      <div class="stats-cards" v-show="!error">
        <StatCard 
          icon="📊"
          title="총 컨텐츠 수"
          :value="totalViewCounts"
          :format-number="true"
        />
        <StatCard 
          icon="👁️"
          title="평균 조회수"
          :value="averageViewCount"
          :format-number="true"
        />
        <StatCard 
          icon="🎯"
          title="선택된 타입"
          :value="selectedContentTypeLabel"
        />
      </div>

      <!-- 데이터 테이블 -->
      <DataTable
        title="조회수 통계 목록"
        :columns="tableColumns"
        :data="viewCounts"
        :loading="isLoading"
        :row-key="getRowKey"
        loading-text="로딩 중..."
        empty-text="통계 데이터가 없습니다."
      >
        <!-- 컨텐츠 타입 컬럼 -->
        <template #cell-contentType="{ value }">
          <ContentTypeBadge :type="value" />
        </template>

        <!-- 조회수 컬럼 -->
        <template #cell-viewCount="{ value }">
          <span class="view-count">{{ formatNumber(value) }}</span>
        </template>

        <!-- 생성일 컬럼 -->
        <template #cell-createdAt="{ value }">
          <span class="timestamp">{{ formatDateTime(value) }}</span>
        </template>

        <!-- 상태 컬럼 -->
        <template #cell-isActive="{ value }">
          <span :class="['status-badge', { active: value, inactive: !value }]" :title="`상태값: ${value} (타입: ${typeof value})`">
            {{ value ? '활성' : '비활성' }}
          </span>
        </template>
        
        <!-- 작성자 컬럼 -->
        <template #cell-authorName="{ value }">
          <span class="author-name">{{ value || '-' }}</span>
        </template>
      </DataTable>

      <!-- 페이지네이션 -->
      <Pagination
        :current-page="pagination.page"
        :total-pages="pagination.totalPages"
        :total-elements="pagination.totalElements"
        :page-size="pagination.size"
        :has-next="pagination.hasNext"
        :has-previous="pagination.hasPrevious"
        @page-change="changePage"
        @page-size-change="changePageSize"
        @first-page="goToFirstPage"
        @last-page="goToLastPage"
        @previous-page="goToPreviousPage"
        @next-page="goToNextPage"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import StatCard from '@/components/ui/StatCard.vue'
import DataTable from '@/components/ui/DataTable.vue'
import FilterSection from '@/components/ui/FilterSection.vue'
import ContentTypeBadge from '@/components/ui/ContentTypeBadge.vue'
import Pagination from '@/components/ui/Pagination.vue'
import { useViewCounts } from '@/composables/useViewCounts'
import { 
  formatDateTime, 
  formatNumber, 
  getContentTypeLabel
} from '@/utils/formatters'

// Composables
const {
  viewCounts,
  selectedContentType,
  isLoading,
  error,
  pagination,
  sortBy,
  sortDirection,
  totalViewCounts,
  averageViewCount,
  fetchViewCounts,
  refreshData,
  changePage,
  changePageSize,
  changeSort,
  changeContentType,
  goToFirstPage,
  goToLastPage,
  goToPreviousPage,
  goToNextPage
} = useViewCounts()

// 컨텐츠 타입 옵션 (전체 제외)
const contentTypeOptions = [
  { value: 'ARTIST', label: '아티스트' },
  { value: 'ARTWORK', label: '작품' },
  { value: 'EVENT', label: '이벤트' },
  { value: 'EXHIBITION', label: '전시' },
  { value: 'NEWS', label: '뉴스' },
  { value: 'SPECIAL', label: '스페셜' }
]

// 선택된 컨텐츠 타입 라벨
const selectedContentTypeLabel = computed(() => {
  const option = contentTypeOptions.find(opt => opt.value === selectedContentType.value)
  return option ? option.label : selectedContentType.value
})

// 테이블 컬럼 정의
const tableColumns = [
  { key: 'contentId', title: 'ID', width: '80px' },
  { key: 'contentTitle', title: '제목', minWidth: '200px' },
  { key: 'contentType', title: '타입', width: '100px' },
  { key: 'viewCount', title: '조회수', width: '100px' },
  { key: 'createdAt', title: '생성일', width: '160px' },
  { key: 'authorName', title: '작성자', width: '120px' },
  { key: 'isActive', title: '상태', width: '80px' }
]

// 테이블 행 키 생성
const getRowKey = (item) => {
  return `${item.contentType}-${item.contentId}`
}

// 정렬 변경 핸들러
const handleSortChange = (event) => {
  const newSortBy = event.target.value
  changeSort(newSortBy, sortDirection.value)
}

const handleSortDirectionChange = (event) => {
  const newSortDirection = event.target.value
  changeSort(sortBy.value, newSortDirection)
}

// 초기 데이터 로드
onMounted(() => {
  fetchViewCounts()
})
</script>

<style scoped>
.view-count-analysis {
  padding: 0;
  width: 100%;
}

.page-header {
  margin-bottom: 32px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 8px;
}

.page-description {
  font-size: 16px;
  color: #666;
  margin: 0;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

.filter-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.filter-item label {
  font-weight: 600;
  color: #333;
  min-width: 80px;
}

.form-select {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  min-width: 120px;
}

.refresh-button {
  padding: 8px 16px;
  border: 1px solid #007bff;
  border-radius: 4px;
  background: #007bff;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  transition: all 0.2s;
}

.refresh-button:hover:not(:disabled) {
  background: #0056b3;
}

.refresh-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.error-message {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  color: #721c24;
  margin-bottom: 24px;
}

.error-icon {
  font-size: 18px;
}

.error-text {
  flex: 1;
  font-weight: 500;
}

.retry-button {
  padding: 6px 12px;
  border: 1px solid #721c24;
  border-radius: 4px;
  background: transparent;
  color: #721c24;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
}

.retry-button:hover {
  background: #721c24;
  color: white;
}

.view-count {
  font-weight: 600;
  color: #007bff;
}


.timestamp {
  font-size: 13px;
  color: #666;
  white-space: nowrap;
}

.author-name {
  font-size: 14px;
  color: #333;
  display: inline-block;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.active {
  background: #d4edda;
  color: #155724;
}

.status-badge.inactive {
  background: #f8d7da;
  color: #721c24;
}

@media (max-width: 768px) {
  .stats-cards {
    grid-template-columns: 1fr;
  }
  
  .filter-item {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filter-item label {
    min-width: auto;
  }

  .refresh-button {
    padding: 10px 16px;
    font-size: 16px;
  }

  .error-message {
    flex-direction: column;
    align-items: stretch;
    text-align: center;
  }

  .retry-button {
    align-self: center;
    padding: 8px 16px;
    font-size: 14px;
  }
}
</style>